/** One catalog field of one record, in view or edit mode, with the field-state chrome
 *  of plan §1.3.
 *
 *  Zero re-layout: the slot is the same three parts in both modes — a label row, a
 *  control part (min height of a small outlined input) and a caption part (one line
 *  reserved) — so switching to edit swaps only the control's content and a change
 *  caption never pushes the grid. A vendor / derived slot (identical in both modes, never
 *  captioned) reserves neither. View mode shows the STORED value, never the draft's.
 *
 *  States (edit mode), each with an icon AND text, never colour alone:
 *  - changed — warning tint α 0.12 (the `PENDING_CELL_SX` precedent), "was …" caption,
 *    revert button;
 *  - feed-controlled — a muted sync icon after the label, its verdict and evidence in
 *    the tooltip / accessible name; changed + will be reverted turns it amber and the
 *    caption adds "<Restech>'s next update will undo this" (a catalog record's source:
 *    "<Source>'s next sync …" — `config.feedEvent`). A changed field reads the
 *    verdict the PREVIEW of the current draft gives its change (the server plans a
 *    queued check-in's freeze into it), else the record's stored one;
 *  - suspended — an info icon with the server's evidence for the pause;
 *  - needs a choice — the question renders in the caption part, the same component as
 *    the ledger's (a field grid may take it out to a row of its own, `inlineChoices`);
 *  - invalid — the error under the input (client checks, 400 errors, preview conflicts,
 *    unparseable input);
 *  - stale — the struck baseline, "Changed on the server to X by Y at Z", Keep mine /
 *    Take theirs;
 *  - read-only — plain text (muted for vendor / derived), provenance on hover.
 *
 *  An action field (on-site status, pre-check-in state, owner) is read-only text with its
 *  action control beside the value (`trailing`) — queue in edit mode, "… now" when the
 *  draft is clean. A change's own warnings (`config.changeWarnings`) and a conflict's
 *  suggested fix (`config.fixFor`) sit in its caption, and so does a standing note from the
 *  record's other values (`config.fieldNoteOf`, in both modes: "Not used with Phone Number" —
 *  the value kept, dimmed, still editable).
 *
 *  A ledger row focuses the slot through the editor's focus registry. A record with no
 *  baseline yet (a row the draft creates) shows `config.defaultValueOf`; reverting also
 *  reverts what the record kind cascades from the field (`config.cascadeTargets`).
 */
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ReplayIcon from '@mui/icons-material/Replay';
import SyncIcon from '@mui/icons-material/Sync';
import SyncProblemIcon from '@mui/icons-material/SyncProblem';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { alpha, Box, Button, IconButton, Tooltip, Typography, type SxProps, type Theme } from '@mui/material';
import { useCallback, useEffect, useId, useRef, type ReactNode } from 'react';

import { FieldHelp } from '@/components/FieldHelp';
import { FieldActionsAdornment } from '@/components/ValueActions/FieldActionsAdornment';
import { PickerLinks } from '@/components/ValueActions/PickerLinks';
import { ValueActions, type ValueAction } from '@/components/ValueActions/ValueActions';
import { formatZoned } from '@/utils/zonedTime';

import { changeKey, recordKey } from '../draft/changeSet';
import { useDraft, useDraftField } from '../draft/DraftProvider';
import { anchorKey } from '../editor/choices';
import { EMPTY_DISPLAY, isEmptyValue } from '../editor/format';
import { useRecordEditor } from '../editor/RecordEditorProvider';
import type { RecordField, RecordVerdict, RecordWidget } from '../editor/types';

import { ChoiceQuestion } from './ChoiceQuestion';
import { ConflictFixes } from './ConflictFixes';
import { TEMPORAL_WIDGETS } from './widgets';
import { tagChipItems } from './widgets/pickers';
import { TagChips } from './widgets/TagChips';

/** The `PENDING_CELL_SX` tint (config-batch TenantValueTable). */
export const CHANGED_TINT = (t: Theme) => alpha(t.palette.warning.main, 0.12);

/** Height of a small outlined input — the control part reserves it in both modes. */
const CONTROL_MIN_HEIGHT = 40;
/** One caption line, reserved in both modes. */
const CAPTION_MIN_HEIGHT = 20;
/** The caption part's top padding, and the line under it. */
const CAPTION_PAD_PX = 2;
const CAPTION_LINE_PX = CAPTION_MIN_HEIGHT - CAPTION_PAD_PX;

/** Long text, read as written (line breaks kept) and capped at 18 lines with a scroll —
 *  in view mode, and in a lazy editor's edit mode before it opens (the same box). */
export const LONG_TEXT_WIDGETS: ReadonlySet<string> = new Set(['textarea', 'text_lazy']);

/** Widgets whose value actions ride in the input's end adornment. */
const ADORNED_WIDGETS = new Set(['text', 'email']);

const TINT_TRANSITION = {
  transition: 'background-color 150ms ease-out',
  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
} as const;

export interface FieldSlotProps {
  /** Catalog key. */
  fieldKey: string;
  entity: string;
  id: number;
  /** Draft path (default: the key; an action field travels as `action:<key>`). */
  path?: string;
  /** Label override (a party row's column). */
  label?: string;
  /** Read-only content for vendor / derived fields — what the panel renders from the
   *  record detail. */
  display?: ReactNode;
  /** Value actions of a linkable value, built from the live value. */
  actions?: (value: unknown) => ValueAction[];
  /** Beside the value in both modes — an action field's control (`ActionControl`). */
  trailing?: ReactNode;
  /** false: the field's questions are not in its caption — the field grid renders them
   *  on a row of their own under the field's row (`FieldCellGrid`). Default true. */
  inlineChoices?: boolean;
}

export function FieldSlot(props: FieldSlotProps) {
  const { fieldOf } = useRecordEditor();
  const field = fieldOf(props.entity, props.fieldKey);
  if (!field) {
    // A registry key the catalog does not know (the contract test catches it in CI).
    return (
      <Box data-field-slot={props.fieldKey} data-state="unknown">
        <Typography variant="caption" color="text.secondary">
          {props.label ?? props.fieldKey}
        </Typography>
        <Typography variant="body2" color="text.disabled">
          {EMPTY_DISPLAY}
        </Typography>
      </Box>
    );
  }
  return <FieldSlotInner {...props} field={field} />;
}

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  } catch {
    return false;
  }
}

const FOCUSABLE =
  'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), [role="combobox"], [tabindex="0"], button:not([disabled])';

function verdictText(verdict: RecordVerdict, managedBy: string, suspended: string, event: string): string {
  // The server's words for the pause; the generic sentence only when it gave none.
  if (verdict.verdict === 'suspended') return verdict.evidence || suspended;
  if (verdict.verdict === 'overwrites') {
    return verdict.evidence
      ? `${verdict.evidence} — its next ${event} overwrites manual values`
      : `${managedBy}'s next ${event} overwrites manual values`;
  }
  return verdict.evidence || `${managedBy} rewrites this whenever it sends it`;
}

function FieldSlotInner({
  field,
  fieldKey,
  entity,
  id,
  path: pathProp,
  label: labelProp,
  display,
  actions,
  trailing,
  inlineChoices = true,
}: FieldSlotProps & { field: RecordField }) {
  const editor = useRecordEditor();
  const {
    mode,
    config,
    pathOf,
    widgetOf,
    formatValue,
    validateValue,
    registerFocus,
    raised,
    errorsFor,
    setInvalidInput,
    preview,
  } = editor;
  const { state, dispatch, baselineOf } = useDraft();
  const rec = recordKey(entity, id);
  const path = pathProp ?? pathOf(field);
  const draft = useDraftField(entity, id, path);
  const editing = mode === 'edit';
  const label = labelProp ?? field.label;
  const managedBy = config.managedBy ?? 'The feed';
  const feedEvent = config.feedEvent ?? 'update';

  const uid = useId();
  const inputId = `${uid}-input`;
  const labelId = `${uid}-label`;
  const captionId = `${uid}-caption`;
  const rootRef = useRef<HTMLDivElement>(null);
  const controlRef = useRef<HTMLDivElement>(null);

  const editability = config.editabilityOf?.(rec, field, state) ?? { editable: true };
  const isInput = field.kind === 'editable' && field.widget !== null;
  const Widget = widgetOf(field);
  const canEdit = editing && isInput && editability.editable && Widget !== undefined;

  const changed = editing && draft.changed;
  const stale = editing ? draft.stale : undefined;
  const outcome = changed ? preview.data?.changes.find((c) => c.key === changeKey(rec, path)) : undefined;
  const verdict =
    editing && (field.kind === 'editable' || field.kind === 'action')
      ? (outcome?.verdict ?? config.verdictOf(rec, field, state))
      : null;
  const warnings = changed && draft.entry ? (config.changeWarnings?.(rec, path, draft.entry.to) ?? []) : [];
  const feed = verdict && verdict.verdict !== 'kept' ? verdict : null;
  const willRevert = changed && feed?.verdict === 'overwrites';
  const suspendedText = config.suspendedText?.(Boolean(feed?.queued)) ?? `${managedBy} updates are paused`;
  const validation = changed && draft.entry ? validateValue(field, draft.entry.to, draft.entry.from) : null;
  const errors = editing ? [...(validation ? [validation] : []), ...errorsFor(rec, path)] : [];
  const choices = editing ? raised.filter((c) => c.anchor === anchorKey(rec, path)) : [];
  // A record without a baseline yet (a row the draft creates) shows its defaults.
  const baseline = draft.baseline === undefined && config.defaultValueOf ? config.defaultValueOf(rec, path) : draft.baseline;
  // View mode never shows draft values.
  const value = editing ? (draft.entry ? draft.entry.to : baseline) : baseline;
  // What the record's other values say about this one (the draft's in edit mode).
  const note = config.fieldNoteOf?.(rec, field, (p) => {
    const entry = editing ? state.entries[rec]?.[p] : undefined;
    return entry ? entry.to : baselineOf(rec, p);
  }) ?? null;
  const revert = () =>
    dispatch({ type: 'REVERT', targets: [{ rec, path }, ...(config.cascadeTargets?.(rec, path, state) ?? [])] });

  // ---- focus handle (ledger rows) ----------------------------------------
  useEffect(
    () =>
      registerFocus(rec, path, () => {
        const root = rootRef.current;
        if (!root) return;
        root.scrollIntoView?.({ block: 'center', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
        const target = controlRef.current?.querySelector<HTMLElement>(FOCUSABLE) ?? root;
        target.focus({ preventScroll: true });
      }),
    [registerFocus, rec, path],
  );

  // An input the draft could not hold is forgotten once the value moves (an undo) or
  // the input goes away (view mode).
  const valueKey = JSON.stringify(draft.value ?? null);
  useEffect(() => {
    setInvalidInput(rec, path, null);
  }, [valueKey, canEdit, rec, path, setInvalidInput]);
  const onInvalid = useCallback(
    (message: string | null) => setInvalidInput(rec, path, message),
    [setInvalidInput, rec, path],
  );

  const states = [
    changed && 'changed',
    stale && 'stale',
    errors.length > 0 && 'invalid',
    feed && feed.verdict !== 'suspended' && 'feed',
    willRevert && 'will-revert',
    feed?.verdict === 'suspended' && 'suspended',
    !canEdit && 'readonly',
    choices.length > 0 && 'needs-choice',
    note?.unused && 'unused',
  ].filter(Boolean);

  const readOnlyKind = field.kind === 'vendor' || field.kind === 'derived';
  // A vendor / derived value is the same text in both modes and never carries a caption,
  // so it reserves neither the input height nor the caption line.
  const compact = readOnlyKind;
  const liveActions = actions && !isEmptyValue(value) ? actions(value) : [];

  // ---- label row ---------------------------------------------------------
  // The record's zone labels its own wall-clock values; a person's date (date of birth)
  // has no zone.
  const zone =
    field.widget && TEMPORAL_WIDGETS.has(field.widget) && field.entity !== 'user' ? config.timezone : null;
  const help = [field.help, field.warning].filter(Boolean).join(' ');
  const feedIcon = feed ? (
    <Tooltip title={verdictText(feed, managedBy, suspendedText, feedEvent)} describeChild>
      <Box
        component="span"
        role="img"
        tabIndex={0}
        aria-label={`${feed.verdict === 'suspended' ? 'Paused' : `From ${managedBy}`}: ${verdictText(feed, managedBy, suspendedText, feedEvent)}`}
        data-verdict={feed.verdict}
        sx={{ display: 'inline-flex', borderRadius: 0.5, '&:focus-visible': { outline: 2, outlineColor: 'primary.main' } }}
      >
        {feed.verdict === 'suspended' ? (
          <InfoOutlinedIcon sx={{ fontSize: 16, color: 'info.main' }} />
        ) : (
          <SyncIcon sx={{ fontSize: 16, color: willRevert ? 'warning.main' : 'text.secondary' }} />
        )}
      </Box>
    </Tooltip>
  ) : null;
  const wasText = draft.entry ? formatValue(field, rec, draft.entry.from) : '';
  // The label wraps (never cut), so the edit-mode icons keep their room in view mode —
  // at the row's end, out of sight: a label breaks the same way in both modes and Edit
  // moves no row.
  const changeable = field.kind === 'editable' || field.kind === 'action';
  const viewVerdict = !editing && changeable ? config.verdictOf(rec, field, state) : null;
  const feedRoom = viewVerdict && viewVerdict.verdict !== 'kept' ? <Box component="span" aria-hidden sx={ICON_ROOM_SX} /> : null;
  const showRevert = changed && !stale;

  // A list widget (the contacts) marks its own items: their errors, changes and questions.
  // A widget may own only its errors (the opening hours: on the day they concern) — the
  // caption then shows none of them, so no error is shown twice.
  const listWidget = Widget?.owns === 'items' && canEdit;
  const ownsErrors = Widget?.owns !== undefined && canEdit;

  // ---- control -----------------------------------------------------------
  let control: ReactNode;
  if (canEdit && Widget) {
    const endAdornment =
      field.widget && ADORNED_WIDGETS.has(field.widget) && liveActions.length > 0 ? (
        <FieldActionsAdornment actions={liveActions} label={`${label} links`} />
      ) : undefined;
    control = (
      <Box
        sx={[
          { width: '100%', minWidth: 0, '& .MuiOutlinedInput-root': TINT_TRANSITION },
          // A tag set's chips carry its change; its "Add a tag" search stays plain. A list
          // widget tints only the items that changed (amber = pending), never its kept rows.
          changed && field.widget !== 'tags' && !listWidget && { '& .MuiOutlinedInput-root': { bgcolor: CHANGED_TINT } },
          changed && field.widget === 'boolean' && { bgcolor: CHANGED_TINT, borderRadius: 1, px: 0.5 },
          // A lazy text editor that is not open yet shows its text in place of an input; so
          // does a rich-text box until it is clicked.
          changed && { '& [data-text-lazy], & [data-richtext="closed"]': { bgcolor: CHANGED_TINT } },
          // Not used as things stand: kept, dimmed, still editable.
          Boolean(note?.unused) && { '& .MuiInputBase-input, & .MuiSelect-select': { color: 'text.secondary' } },
        ]}
      >
        <Widget
          field={field}
          rec={rec}
          id={inputId}
          labelId={labelId}
          describedBy={captionId}
          value={value}
          baseline={baseline}
          // A row the draft creates changes from its defaults, so editing back to one is
          // no change.
          onChange={(v, opts) => draft.set(v, draft.baseline === undefined ? { ...opts, from: baseline } : opts)}
          onFocus={draft.beginCoalesce}
          onBlur={draft.endCoalesce}
          onInvalid={onInvalid}
          error={errors.length > 0}
          disabled={false}
          timezone={config.timezone}
          endAdornment={endAdornment}
        />
      </Box>
    );
  } else {
    control = (
      <ReadValue
        field={field}
        rec={rec}
        value={value}
        baseline={baseline}
        // The panel's rendering of the stored value — never over a draft value.
        display={readOnlyKind || !changed ? display : undefined}
        actions={liveActions}
        muted={readOnlyKind || (editing && !editability.editable) || Boolean(note?.unused)}
        tooltip={
          readOnlyKind
            ? (config.provenanceOf?.(field, rec) ??
              (field.kind === 'vendor' ? `Set by ${config.managedBy ?? 'the feed'}` : (field.help ?? null)))
            : editing && !editability.editable && !note?.locks
              ? (editability.reason ?? null)
              : null
        }
        tint={changed}
        fill={!trailing}
        View={Widget?.View}
        reserveBelow={Widget?.reserveBelow}
      />
    );
  }

  // ---- caption -----------------------------------------------------------
  const caption: ReactNode[] = [];
  if (stale?.gone) {
    // The record is gone on the server: there is nothing to keep the change on.
    caption.push(
      <Box key="stale" data-caption="stale" data-gone sx={{ display: 'grid', gap: 0.5 }}>
        <Typography variant="caption" component="div" sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }}>
          <SyncProblemIcon aria-hidden sx={{ fontSize: 16, color: 'warning.main', mt: '1px' }} />
          <span>Deleted elsewhere — this edit can’t be saved</span>
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Button size="small" variant="outlined" onClick={draft.takeTheirs} sx={{ py: 0, minHeight: 24 }}>
            Drop this edit
          </Button>
        </Box>
      </Box>,
    );
  } else if (stale) {
    const by = stale.changedBy;
    const when = by?.ts ? formatZoned(by.ts, config.timezone, { weekday: false }) : null;
    caption.push(
      <Box key="stale" data-caption="stale" sx={{ display: 'grid', gap: 0.5 }}>
        <Typography variant="caption" component="div" sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }}>
          <SyncProblemIcon aria-hidden sx={{ fontSize: 16, color: 'warning.main', mt: '1px' }} />
          <span>
            <Box component="s" sx={{ color: 'text.secondary' }}>
              {wasText}
            </Box>{' '}
            Changed on the server to <b>{formatValue(field, rec, stale.current)}</b>
            {by?.label ? ` by ${by.label}` : ''}
            {when ? ` ${when}` : ''}
          </span>
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Button size="small" variant="outlined" onClick={draft.keepMine} sx={{ py: 0, minHeight: 24 }}>
            Keep mine
          </Button>
          <Button size="small" onClick={draft.takeTheirs} sx={{ py: 0, minHeight: 24 }}>
            Take theirs
          </Button>
        </Box>
      </Box>,
    );
  }
  if (errors.length > 0 && !ownsErrors) {
    caption.push(
      <Typography
        key="errors"
        data-caption="invalid"
        variant="caption"
        component="div"
        color="error"
        sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }}
      >
        <ErrorOutlineIcon aria-hidden sx={{ fontSize: 16, mt: '1px' }} />
        <span>{errors.join(' · ')}</span>
      </Typography>,
      <ConflictFixes key="fixes" rec={rec} path={path} />,
    );
  }
  if (changed && !stale && (!listWidget || willRevert)) {
    caption.push(
      <Typography
        key="changed"
        data-caption="changed"
        variant="caption"
        component="div"
        color="text.secondary"
        sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}
      >
        {!listWidget && <span>was {wasText}</span>}
        {willRevert && (
          <Box component="span" sx={{ display: 'inline-flex', gap: 0.25, alignItems: 'flex-start', color: 'text.primary' }}>
            <SyncIcon aria-hidden sx={{ fontSize: 14, color: 'warning.main', mt: '2px', flex: 'none' }} />
            {`${managedBy}'s next ${feedEvent} will undo this`}
          </Box>
        )}
      </Typography>,
    );
  }
  // A list widget's change is its items' (each says its own state); its warnings are on
  // the ledger row.
  for (const w of listWidget ? [] : warnings) {
    caption.push(
      <Typography
        key={`warning:${w}`}
        data-caption="warning"
        variant="caption"
        component="div"
        sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }}
      >
        <WarningAmberIcon aria-hidden sx={{ fontSize: 14, color: 'warning.main', mt: '2px', flex: 'none' }} />
        <span>{w}</span>
      </Typography>,
    );
  }
  if (note) {
    const caution = note.tone === 'caution';
    caption.push(
      <Typography
        key="note"
        data-caption="note"
        variant="caption"
        component="div"
        color={caution ? 'text.primary' : 'text.secondary'}
        sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }}
      >
        {caution ? (
          <WarningAmberIcon aria-hidden sx={{ fontSize: 14, color: 'warning.main', mt: '2px', flex: 'none' }} />
        ) : note.locks ? (
          <LockOutlinedIcon aria-hidden sx={{ fontSize: 14, mt: '2px', flex: 'none' }} />
        ) : (
          <InfoOutlinedIcon aria-hidden sx={{ fontSize: 14, mt: '2px', flex: 'none' }} />
        )}
        <span>{note.text}</span>
      </Typography>,
    );
  }
  if (feed && !willRevert) {
    // The verdict in words for screen readers (the icon's tooltip is the visual).
    caption.push(
      <Box key="verdict" component="span" sx={VISUALLY_HIDDEN}>
        {verdictText(feed, managedBy, suspendedText, feedEvent)}
      </Box>,
    );
  }
  if (canEdit && liveActions.length > 0 && field.widget && !ADORNED_WIDGETS.has(field.widget)) {
    caption.push(
      // Exactly the reserved caption line, which view mode keeps empty: no top margin of its
      // own, and its line no taller than the line (a caption's 20 px line box would grow the
      // slot by its top padding) — so pressing E moves nothing.
      <PickerLinks
        key="links"
        sx={{ mt: 0, height: CAPTION_LINE_PX, lineHeight: `${CAPTION_LINE_PX}px`, '& *': { lineHeight: 'inherit' } }}
        items={[{ key: String(value), label: formatValue(field, rec, value), actions: liveActions }]}
      />,
    );
  }
  for (const c of inlineChoices ? choices : []) {
    caption.push(
      <Box key={`choice:${c.option.key}`} sx={{ mt: 0.5 }}>
        <ChoiceQuestion choice={c} />
      </Box>,
    );
  }

  return (
    <Box
      ref={rootRef}
      tabIndex={-1}
      data-field-slot={fieldKey}
      data-rec={rec}
      data-state={states.join(' ') || undefined}
      sx={{
        // Contains the visually hidden verdict text (absolute) inside the slot.
        position: 'relative',
        minWidth: 0,
        outline: 'none',
        borderRadius: 1,
        '&:focus-visible': { boxShadow: (t) => `0 0 0 2px ${t.palette.primary.main}` },
      }}
    >
      <Box data-part="label" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minHeight: 24, minWidth: 0 }}>
        <Typography
          component="label"
          id={labelId}
          {...(canEdit ? { htmlFor: inputId } : {})}
          variant="caption"
          color="text.secondary"
          sx={{ minWidth: 0 }}
        >
          {label}
        </Typography>
        {zone && (
          <Typography variant="caption" color="text.secondary" noWrap>
            · {zone}
          </Typography>
        )}
        {feedIcon}
        {help && <FieldHelp help={{ title: label, body: help }} />}
        <Box sx={{ flex: 1 }} />
        {feedRoom}
        {showRevert ? (
          <Tooltip title={`Revert to ${wasText}`}>
            <IconButton size="small" aria-label={`Revert ${label}`} onClick={revert} sx={{ p: 0.25 }}>
              <ReplayIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        ) : (
          changeable && <Box component="span" aria-hidden sx={REVERT_ROOM_SX} />
        )}
      </Box>
      <Box
        ref={controlRef}
        data-part="control"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          minHeight: compact ? 0 : CONTROL_MIN_HEIGHT,
          minWidth: 0,
        }}
      >
        {control}
        {trailing && (
          <Box data-part="trailing" sx={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {trailing}
          </Box>
        )}
      </Box>
      <Box
        id={captionId}
        data-part="caption"
        sx={{
          minHeight: compact ? 12 : CAPTION_MIN_HEIGHT,
          display: 'grid',
          gap: 0.25,
          alignContent: 'start',
          pt: compact ? 0 : `${CAPTION_PAD_PX}px`,
        }}
      >
        {caption}
      </Box>
    </Box>
  );
}

/** The room of the label row's feed icon (16 px) and revert button (16 px icon + 2 × 2 px
 *  padding) while they are not shown. */
const ICON_ROOM_SX = { width: 16, height: 16, flex: 'none' } as const;
const REVERT_ROOM_SX = { width: 20, height: 20, flex: 'none' } as const;

// px strings: in sx a bare `1` is 100%.
const VISUALLY_HIDDEN: SxProps<Theme> = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  m: '-1px',
  p: 0,
  border: 0,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
};

function ReadValue({
  field,
  rec,
  value,
  baseline,
  display,
  actions,
  muted,
  tooltip,
  tint,
  fill,
  View,
  reserveBelow,
}: {
  field: RecordField;
  rec: string;
  value: unknown;
  baseline: unknown;
  display: ReactNode | undefined;
  actions: ValueAction[];
  muted: boolean;
  tooltip: string | null;
  tint: boolean;
  /** Take the slot's width, as an input does — a list value spans it. Off beside an
   *  action control, which sits right after the value. */
  fill: boolean;
  /** The widget's own read-only rendering (`RecordWidgetProtocol.View`). */
  View: RecordWidget['View'];
  /** The room kept under the value for what only the input adds (`reserveBelow`). */
  reserveBelow: number | undefined;
}) {
  const { formatValue, optionRef } = useRecordEditor();
  let content: ReactNode;
  let empty = false;
  if (display !== undefined) {
    content = display;
    empty = display === null || display === '';
  } else if (View) {
    // It says "empty" in its own shape (seven closed days, the rich-text box's dash).
    content = <View field={field} value={value} />;
  } else if (field.widget === 'tags') {
    content = (
      <TagChips label={field.label} items={tagChipItems(field, tint ? baseline : value, value, optionRef, { merged: tint })} />
    );
  } else {
    const text = formatValue(field, rec, value);
    empty = text === EMPTY_DISPLAY;
    content = text;
  }
  if (empty) content = EMPTY_DISPLAY;
  const node = (
    <Typography
      variant="body2"
      component="div"
      sx={[
        {
          flex: fill ? '1 1 auto' : '0 1 auto',
          minWidth: 0,
          overflowWrap: 'anywhere',
          color: empty ? 'text.disabled' : muted ? 'text.secondary' : 'text.primary',
          ...TINT_TRANSITION,
        },
        LONG_TEXT_WIDGETS.has(field.widget ?? '') && { whiteSpace: 'pre-wrap', maxHeight: '18em', overflowY: 'auto' },
        // The lazy editor's edit icon sits at the text's end in edit mode: its room is kept
        // here too, so the text wraps the same in both modes (Zero-Re-Layout).
        field.widget === 'text_lazy' && { pr: 3 },
        tint && { bgcolor: CHANGED_TINT, borderRadius: 1, px: 0.75, py: 0.25 },
      ]}
    >
      {!empty && actions.length > 0 ? <ValueActions actions={actions}>{content}</ValueActions> : content}
      {reserveBelow ? <Box aria-hidden data-reserved-below sx={{ height: reserveBelow }} /> : null}
    </Typography>
  );
  return tooltip ? (
    <Tooltip title={tooltip} placement="bottom-start" describeChild>
      {node}
    </Tooltip>
  ) : (
    node
  );
}
